import React from "react";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import StorageIcon from "@mui/icons-material/Storage";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { IMixedSubQueryV2 } from "./mixedQueryV2.utils";

interface SourceReferenceCardProps {
  item: IMixedSubQueryV2;
  index: number;
  label: string;
  // Available column names for this source (for reference while writing SQL).
  columnNames: string[];
  // Insert a column token into the currently-active editor.
  onColumnClick?: (column: string) => void;
  // Resolves a raw dependency marker (e.g. "Sub_Query_1") to a user-friendly
  // label so dependency chips read meaningfully.
  getDependencyDisplayLabel: (marker: string) => string;
}

// Read-only reference card shown on top of the expanded editor (Figma
// "Query Editor - v2"). It lets the user see a selected source's query and
// columns while editing another query, so they can reference it.
const SourceReferenceCard: React.FC<SourceReferenceCardProps> = ({
  item,
  index,
  label,
  columnNames,
  onColumnClick,
  getDependencyDisplayLabel,
}) => {
  const typeLabel =
    item?.sub_type === "External" ? "External Queries" : "Resource Queries";
  const dependencies = item?.dependency_list || [];

  const handleCopy = () => {
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(`<${label}>`);
    }
  };

  return (
    <Box
      className={`mixed-v2-ref-card ${
        item?.is_active === false ? "mixed-v2-ref-card--inactive" : ""
      }`}
    >
      <Box className="mixed-v2-ref-card__head">
        <StorageIcon fontSize="small" className="mixed-v2-ref-card__icon" />
        <Typography className="mixed-v2-ref-card__name" title={label}>
          {label}
        </Typography>
        <Chip
          size="small"
          className="mixed-v2-ref-card__type"
          label={typeLabel}
        />
        <Box className="mixed-v2-ref-card__head-right">
          {dependencies.length > 0 && (
            <Box className="mixed-v2-ref-card__deps">
              <Typography className="mixed-v2-ref-card__deps-label">
                Depends on:
              </Typography>
              {dependencies.map((marker) => {
                const depLabel = getDependencyDisplayLabel(marker);
                return (
                  <Tooltip
                    key={marker}
                    title={`Depends on ${depLabel}`}
                    placement="top"
                    arrow
                  >
                    <Chip
                      size="small"
                      className="mixed-v2-ref-card__dep-chip"
                      label={depLabel}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      <Box className="mixed-v2-ref-card__sql">
        <pre>{item?.data_pull_query || ""}</pre>
      </Box>

      {columnNames.length > 0 && (
        <Box className="mixed-v2-ref-card__columns">
          <Typography className="mixed-v2-ref-card__columns-label">
            Columns — click to insert:
          </Typography>
          <Box className="mixed-v2-ref-card__chips">
            {columnNames.map((col, i) => (
              <button
                key={`${col}-${i}`}
                type="button"
                className="mixed-v2-ref-card__chip"
                onClick={() => onColumnClick?.(col)}
              >
                {col}
              </button>
            ))}
          </Box>
        </Box>
      )}

      <Box className="mixed-v2-ref-card__footer">
        <Typography className="mixed-v2-ref-card__ref-label">
          Reference as:
        </Typography>
        <Tooltip title={`<${label}>`} placement="top" arrow>
          <Chip
            size="small"
            className="mixed-v2-ref-card__ref-chip"
            label={label}
          />
        </Tooltip>
        <Tooltip title="Copy reference" placement="top" arrow>
          <IconButton
            size="small"
            className="mixed-v2-ref-card__copy"
            onClick={handleCopy}
          >
            <ContentCopyIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default SourceReferenceCard;
